import { useCallback, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { ConfigurationSelector } from 'store/configuration';
import {
  CallScribeCallsByTicketSelector,
  CallScribeCallsErrorSelector,
  CallScribeCallsLoadingSelector,
  CallScribeTerminalStatusEventCountSelector,
} from 'store/callScribe/selectors';
import {
  ThunkGetCallScribeCallsByContact,
  ThunkGetCallScribeCallsByTicket,
} from 'store/callScribe/thunks';
import { isContactScreen } from 'components/CallScribe/contactScreen';
import { ICallScribeCallListItem } from 'services/callScribe/types';
import { normalizeExternalTicketId } from 'components/CallScribe/ticketId';
import { subscribeToCallStateSignal } from 'components/CallScribe/callEndSignal';
import { isSoftphonePopupView } from 'components/CallScribe/popout';
import { subscribeToPodReturn } from 'components/CallScribe/podReturn';

export interface IUseCallScribeTicketCallsResult {
  calls: ICallScribeCallListItem[];
  loading: boolean;
  error?: string;
  refetch: () => void;
}

export const useCallScribeTicketCalls = (): IUseCallScribeTicketCallsResult => {
  const dispatch = useAppDispatch();
  const config = useAppSelector(ConfigurationSelector);
  const calls = useAppSelector(CallScribeCallsByTicketSelector);
  const loading = useAppSelector(CallScribeCallsLoadingSelector);
  const error = useAppSelector(CallScribeCallsErrorSelector);
  const terminalStatusCount = useAppSelector(CallScribeTerminalStatusEventCountSelector);

  const inPopupMode = isSoftphonePopupView();
  const contactScreen = isContactScreen(config);
  const contactId = config.contactId;

  const loadCalls = useCallback(() => {
    const crmSourceId = config.psaId;
    if (!crmSourceId) return false;
    if (contactScreen) {
      if (!contactId) return false;
      void dispatch(ThunkGetCallScribeCallsByContact({ crmSourceId, contactId }));
      return true;
    }
    const ticketId = normalizeExternalTicketId(config.ticketId);
    if (!ticketId) return false;
    void dispatch(ThunkGetCallScribeCallsByTicket({ crmSourceId, ticketId }));
    return true;
  }, [config.psaId, config.ticketId, contactScreen, contactId, dispatch]);

  useEffect(() => {
    if (inPopupMode) return;
    loadCalls();
  }, [loadCalls, inPopupMode]);

  useEffect(() => {
    if (inPopupMode) return undefined;
    return subscribeToCallStateSignal(() => {
      loadCalls();
    });
  }, [loadCalls, inPopupMode]);

  useEffect(() => {
    if (inPopupMode) return undefined;
    return subscribeToPodReturn(() => {
      loadCalls();
    });
  }, [loadCalls, inPopupMode]);

  useEffect(() => {
    if (inPopupMode || terminalStatusCount === 0) return;
    loadCalls();
  }, [terminalStatusCount, loadCalls, inPopupMode]);

  const refetch = useCallback(() => {
    loadCalls();
  }, [loadCalls]);

  return { calls, loading, error, refetch };
};
